import { clearLocalUserData, setLocalUserData } from "@/utils";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

interface UserData {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  user: {
    id: string;
    full_name: string;
    first_name?: string;
    last_name?: string;
    email: string;
    phone: string | null;
    type: string;
    bc_verified: boolean;
    photo: string;
    photo_thumbnail_url: string;
    permissions: string[];
    login_provider: string;
    token_type: string;
    organization_id: string;
    organization_owner: boolean;
    email_verified: boolean;
    onboarding_step: number;
    organization: {
      id: string;
      name: string;
      tax_id: string;
      logo: string;
      status: string;
    };
    is_suspended: boolean;
    suspension_end_date: string | null;
    suspension_reason: string;
    is_banned: boolean;
    ban_reason: string;
    total_reports_received: number;
  };
  subscription: {
    subscription_id: string;
    customer_id: string;
    plan_name: string;
    product_id: string;
    price_id: string;
    starts_at_ms: number;
    expiration_at_ms: number;
    features: { shortlist: number; job_post: number };
    is_trialing: boolean;
    have_trial: boolean;
    is_cancelled: boolean;
    was_trial_cancelled?: boolean;
  };
}

interface AuthState {
  userData: UserData | null;
}

const initialState: AuthState = {
  userData: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    login: (state, action: PayloadAction<UserData>) => {
      setLocalUserData(action.payload);
      state.userData = action.payload;
    },
    logout: (state) => {
      clearLocalUserData();
      state.userData = null;
    },
    refreshToken: (state, action: PayloadAction<string>) => {
      if (state.userData) {
        const updatedUserData = { ...state.userData, access_token: action.payload };
        setLocalUserData(updatedUserData);
        state.userData = updatedUserData;
      }
    },
    updateName: (state, action: PayloadAction<{ first_name: string; last_name: string }>) => {
      const { first_name, last_name } = action.payload;
      const full_name = `${first_name} ${last_name}`;
      const updatedUserData = {
        ...state.userData,
        user: { ...state.userData.user, full_name, first_name, last_name },
      };
      setLocalUserData(updatedUserData);
      state.userData.user.full_name = full_name;
      state.userData.user.first_name = first_name;
      state.userData.user.last_name = last_name;
    },
    updatePhoneNumber: (state, action: PayloadAction<string>) => {
      const updatedUserData = {
        ...state.userData,
        user: { ...state.userData.user, phone: action.payload },
      };
      setLocalUserData(updatedUserData);
      state.userData.user.phone = action.payload;
    },
    updatePhoto: (state, action: PayloadAction<{ photo: string; photo_thumbnail_url: string }>) => {
      const updatedUserData = {
        ...state.userData,
        user: {
          ...state.userData.user,
          photo: action.payload.photo,
          photo_thumbnail_url: action.payload.photo_thumbnail_url,
        },
      };
      setLocalUserData(updatedUserData);
      state.userData.user.photo = action.payload.photo;
      state.userData.user.photo_thumbnail_url = action.payload.photo_thumbnail_url;
    },
    updateLogo: (state, action: PayloadAction<{ logo: string }>) => {
      const updatedUserData = {
        ...state.userData,
        user: {
          ...state.userData.user,
          organization: {
            ...state.userData.user.organization,
            logo: action.payload.logo,
          },
        },
      };
      setLocalUserData(updatedUserData);
      state.userData.user.organization.logo = action.payload.logo;
    },
    updateSuspensionStatus: (
      state,
      action: PayloadAction<{
        is_suspended: boolean;
        suspension_end_date: string | null;
        suspension_reason?: string;
      }>,
    ) => {
      if (state.userData) {
        const updatedUserData = {
          ...state.userData,
          user: {
            ...state.userData.user,
            is_suspended: action.payload.is_suspended,
            suspension_end_date: action.payload.suspension_end_date,
            suspension_reason:
              action.payload.suspension_reason || state.userData.user.suspension_reason,
          },
        };
        setLocalUserData(updatedUserData);
        state.userData = updatedUserData;
      }
    },
    updateBanStatus: (
      state,
      action: PayloadAction<{
        is_banned: boolean;
        ban_reason?: string;
      }>,
    ) => {
      if (state.userData) {
        const updatedUserData = {
          ...state.userData,
          user: {
            ...state.userData.user,
            is_banned: action.payload.is_banned,
            ban_reason: action.payload.ban_reason || state.userData.user.ban_reason,
          },
        };
        setLocalUserData(updatedUserData);
        state.userData = updatedUserData;
      }
    },
  },
});

export const {
  login,
  logout,
  refreshToken,
  updateName,
  updatePhoneNumber,
  updatePhoto,
  updateLogo,
  updateSuspensionStatus,
  updateBanStatus,
} = authSlice.actions;
export default authSlice.reducer;
